import { useDispatch, useSelector } from "react-redux";
import { FiX, FiStar, FiZap } from "react-icons/fi";
import { closeSubscriptionModal, openPlanSelectionModal } from "../redux/dealerSlice";
import type { RootState, AppDispatch } from "../redux/store";

export default function SubscriptionDetailsModal() {
    const dispatch = useDispatch<AppDispatch>();
    const { isSubscriptionModalOpen, profile } = useSelector((state: RootState) => state.dealer);

    const getRevenueTierName = (revenue: number) => {
        if (revenue >= 10000) return "platinum";
        if (revenue >= 6000) return "gold";
        if (revenue >= 2500) return "silver";
        return "bronze";
    };

    const currentTier = (profile?.revenue !== undefined)
        ? getRevenueTierName(profile.revenue)
        : (profile?.tier && typeof profile.tier === "object")
            ? profile.tier.name.toLowerCase()
            : (profile?.tier as string)?.toLowerCase() || "bronze";

    const getTierIcon = (tier: string, size = 24) => {
        const map: Record<string, string> = {
            platinum: '/platinum.png',
            gold: '/gold.png',
            silver: '/silver.png',
            bronze: '/bronze.png',
        };
        const src = map[tier] ?? '/bronze.png';
        return <img src={src} alt={tier} width={size} height={size} className="object-contain" />;
    };

    if (!isSubscriptionModalOpen) return null;

    const handleUpgradeClick = () => {
        dispatch(closeSubscriptionModal());
        dispatch(openPlanSelectionModal());
    };

    return (
        <div className="fixed inset-0 z-[110] bg-black/60 backdrop-blur-sm flex items-center justify-center p-4">
            <div className="bg-white rounded-3xl shadow-2xl w-full max-w-md animate-in fade-in zoom-in duration-300 overflow-hidden relative">
                {/* Header */}
                <div className="p-4 pb-0 flex items-center justify-between">
                    <h3 className="text-lg font-bold text-gray-800 flex items-center gap-2">
                        {getTierIcon(currentTier)} {profile?.revenue !== undefined ? "My Ranking Status" : "My Tier Status"}
                    </h3>
                    <button
                        onClick={() => dispatch(closeSubscriptionModal())}
                        className="p-1.5 hover:bg-gray-100 rounded-full text-gray-400 transition"
                    >
                        <FiX size={18} />
                    </button>
                </div>

                <div className="p-4 space-y-4">
                    {/* Current Plan Card (Compact) */}
                    <div className="p-5 rounded-2xl bg-gradient-to-br from-gray-900 to-gray-800 text-white shadow-xl relative overflow-hidden">
                        <div className="absolute -right-4 -top-4 w-20 h-20 bg-white/5 rounded-full blur-2xl"></div>
                        <div className="absolute -right-4 -top-4 w-20 h-20 bg-white/5 rounded-full blur-2xl"></div>
                        <p className="text-[10px] font-bold text-white/40 mb-0.5 uppercase tracking-widest">
                            {profile?.revenue !== undefined ? "Current Revenue Tier" : "Current Revenue Tier"}
                        </p>
                        <h4 className="text-2xl font-black capitalize mb-1 flex items-center gap-2">
                            {getTierIcon(currentTier)} {currentTier}
                        </h4>
                        <div className="flex items-center gap-1.5 mb-4">
                            <div className="w-1.5 h-1.5 rounded-full bg-green-400 animate-pulse"></div>
                            <span className="text-[10px] font-bold text-green-300 uppercase tracking-tight">Current Tier</span>
                        </div>

                        <div className="pt-4 border-t border-white/10 flex justify-between items-center">
                            <div>
                                <p className="text-[9px] font-bold text-white/40 uppercase mb-0">
                                    {profile?.revenue !== undefined ? "Status" : "Status"}
                                </p>
                                <p className="text-xs font-bold font-mono text-white/90">
                                    {profile?.revenue !== undefined ? "Revenue Based" : "Revenue Based"}
                                </p>
                            </div>
                            {/* Invoice Button Removed */}
                        </div>
                    </div>

                    {/* Usage Limits (Compact) */}
                    <div className="space-y-3">
                        <h5 className="text-[10px] font-black text-gray-400 uppercase tracking-widest px-1">Usage Tracker</h5>

                        <div className="space-y-2">
                            {/* Photo Limit */}
                            <div className="bg-gray-50 p-3 rounded-xl border border-gray-100">
                                <div className="flex justify-between items-center mb-1.5">
                                    <span className="text-xs font-bold text-gray-600">Photo Uploads</span>
                                    <span className="text-xs font-bold text-gray-500">
                                        {currentTier === 'bronze' ? '3/5' : 'Unlimited'}
                                    </span>
                                </div>
                                <div className="h-1.5 w-full bg-gray-100 rounded-full overflow-hidden">
                                    <div
                                        className="h-full bg-[#B08D55] rounded-full transition-all duration-500 shadow-sm shadow-[#B08D55]/20"
                                        style={{ width: currentTier === 'bronze' ? '60%' : '100%' }}
                                    ></div>
                                </div>
                            </div>

                            {/* Grid Layout for Binary Limits */}
                            <div className="grid grid-cols-2 gap-2">
                                {/* Lead Access */}
                                <div className="flex flex-col gap-1 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                    <div className="flex items-center gap-2 text-gray-500">
                                        <FiStar size={14} className={currentTier === 'bronze' ? 'text-gray-300' : 'text-[#B08D55]'} />
                                        <span className="text-[11px] font-bold">Leads</span>
                                    </div>
                                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md uppercase w-fit ${currentTier === 'bronze' ? 'bg-gray-200 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                                        {currentTier === 'bronze' ? 'Locked' : 'On'}
                                    </span>
                                </div>

                                {/* Priority Search */}
                                <div className="flex flex-col gap-1 p-3 bg-gray-50 rounded-xl border border-gray-100">
                                    <div className="flex items-center gap-2 text-gray-500">
                                        <FiZap size={14} className={['bronze', 'silver'].includes(currentTier) ? 'text-gray-300' : 'text-yellow-500'} />
                                        <span className="text-[11px] font-bold">Search</span>
                                    </div>
                                    <span className={`text-[9px] font-black px-1.5 py-0.5 rounded-md uppercase w-fit ${['bronze', 'silver'].includes(currentTier) ? 'bg-gray-200 text-gray-500' : 'bg-green-100 text-green-700'}`}>
                                        {['bronze', 'silver'].includes(currentTier) ? 'Normal' : 'High'}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    {/* Actions (Compact) */}
                    <div className="pt-1 flex flex-col gap-2">
                        <button
                            onClick={handleUpgradeClick}
                            className="w-full py-3 bg-[#B08D55] text-white text-xs font-black rounded-xl shadow-lg shadow-[#B08D55]/20 hover:bg-[#967645] hover:translate-y-[-1px] transition-all uppercase tracking-wider"
                        >
                            {profile?.revenue !== undefined ? "Get Ranking Boost 🚀" : "View Tiers"}
                        </button>

                    </div>
                </div>
            </div>
        </div>
    );
}
